import React, { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { fetchPrograms, fetchReportPrograms } from "../../../services/httpClient";
import SelectObject from "../../ui/SelectObject";
import DataTable from "../ui/DataTable";
import { loadingCard } from "../../../helpers/loading";

interface ProgramTableProps {
  data?: any[];
  programList?: any[];
  showHeader?: boolean;
}

export const ProgramTable = ({ data, programList, showHeader = false }: ProgramTableProps) => {
  const [programData, setProgramData] = useState<any[]>();
  const [filteredData, setFilteredData] = useState<any>([]);
  const [programs, setPrograms] = useState<any[]>([]);
  const [selectedProgram, setSelectedProgram] = useState<number>();
  const [levelsUsed, setLevelsUsed] = useState<boolean>(false);

  const tableHeaders = [
    { label: "Program ID", key: "program_id", hide: true },
    { label: "Program", key: "program" },
    ...(levelsUsed
      ? [
          { label: "Level ID", key: "level_id", hide: true },
          { label: "Level", key: "level" },
        ]
      : []),
    { label: "Case ID", key: "id", hide: true },
    { label: "Case", key: "case_name" },
    { label: "Name", key: "name" },
  ];

  useEffect(() => {
    const fetchData = async () => {
      if (data && programList) {
        setProgramData(data);
        setFilteredData(data);
        setPrograms(programList);

        const hasLevels = data.some((d: any) => d.level);
        setLevelsUsed(hasLevels);
      } else {
        await getProgramData();
      }
    };

    fetchData();
  }, [data, programList]);

  const getProgramData = async () => {
    fetchReportPrograms()
      .then((res: any) => {
        const progData = res[0]?.data ?? [];
        setProgramData(progData);
        setFilteredData(progData);

        fetchPrograms()
          .then((res: any) => {
            const programs = res[0]?.data ?? [];

            const flattenedProgs = programs.flatMap((prog: any) => {
              const levels = prog.programLevels ?? [];

              // Add level name to each programData entry
              progData.forEach((d: any) => {
                if (d.program_id === prog.id) {
                  const level = levels.find((l: any) => l.id === d.level_id);
                  d.level = level?.name;
                  if (level) {
                    setLevelsUsed(true);
                  }
                }
              });

              const levelEntries = levels.map((level: any) => {
                const levelCount = progData.filter((d: any) => d.level_id === level.id).length;

                return {
                  ...prog,
                  id: `${prog.id}-${level.id}`,
                  level_id: level.id,
                  program_id: prog.id,
                  name: `${prog.name} - ${level.name} (${levelCount})`,
                  key: `${prog.id}-${level.id}`,
                };
              });

              // Always include the base program entry
              const baseCount = progData.filter((d: any) => d.program === prog.name).length;
              const baseProgramEntry = {
                ...prog,
                id: prog.id,
                program_id: prog.id,
                name: `${prog.name} (${baseCount})`,
                key: prog.id,
              };

              return [baseProgramEntry, ...levelEntries];
            });

            setPrograms(flattenedProgs);
          })
          .catch((err: any) => console.log(err));
      })
      .catch((err: any) => console.log(err));
  };

  const updateProgram = (e: any) => {
    setSelectedProgram(e.target.value);
    const filtered = programData?.filter((d: any) => {
      if (e.target.value === "0") {
        return d;
      } else {
        if (e.target.value.includes("-")) {
          // If the value includes a dash, it's a level
          const [programId, levelId] = e.target.value.split("-");
          return d.program_id == programId && d.level_id == levelId;
        }
        return d.program_id == e.target.value;
      }
    });

    setFilteredData(filtered);
  };

  return (
    <>
      {programData ? (
        <>
          {showHeader && (
            <Row>
              <Col>
                <h5 className="app-header-text">Current Programming</h5>
              </Col>
            </Row>
          )}
          {programData.length > 0 && (
            <>
              <Row id="programs-filters">
                <Col xs={4} className="mb-1">
                  <SelectObject
                    label="Program / Level (# People)"
                    initialOption="View All"
                    value={selectedProgram}
                    options={programs}
                    onUpdate={(e: any) => updateProgram(e)}
                  />
                </Col>
              </Row>
              <DataTable tableHeadings={tableHeaders} tableData={filteredData} hideTitle={true} hideDownload={true} />
            </>
          )}
        </>
      ) : (
        loadingCard()
      )}
    </>
  );
};
